"use client";

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Link from "next/link";
import { fetchTrends, trendKeys } from "@/lib/queries";
import { Skeleton } from "@/components/ui/Skeleton";
import { FadeIn } from "@/components/ui/FadeIn";
import { KeywordCard } from "@/components/keyword/KeywordCard";
import { useAuthStore } from "@/store/auth";
import { useBetaStatus } from "@/hooks/useBetaStatus";
import {
  LifecycleStage,
  STAGE_LABEL,
  STAGE_EMOJI,
  STAGE_COLOR,
  STAGE_BG,
} from "@/types";
import { STAGE_META } from "@/lib/lifecycleStages";   // 2026-05-18 stage wording single source
import { SeasonalTagLegend } from "@/components/dashboard/SeasonalTagLegend";

/**
 * 라이프사이클 통합 탭 (사용자 결정 2026-05-08).
 * 옛 StageSection × 4 → 1 섹션 + 탭 4개 (TOP 5).
 *
 * 탭 디자인 — 가로 칩 + stage 색상 (사용자 결정 — 카운트 제거, 가독성 ↑).
 * 활성 탭 아래 stage 설명 박스 (옛 /trends CompactStageDesc 부활).
 */

const STAGES: LifecycleStage[] = ["emerging", "growing", "mature", "declining"];
const TOP_N = 5;


export function LifecycleTabs() {
  const [activeStage, setActiveStage] = useState<LifecycleStage>("emerging");
  const { isLoggedIn, user } = useAuthStore();
  const beta = useBetaStatus();
  // 베타 활성 동안 모두 풀, 종료 후 free_auth 만 6~30위 잠금
  const betaActive = beta?.beta_active ?? true;
  const isPaidLike = !!user && (user.tier === "paid" || user.tier === "franchise");
  const hasFullAccess = betaActive || isPaidLike;

  // 활성 탭의 키워드
  const { data, isPending } = useQuery({
    queryKey: trendKeys.list(activeStage),
    queryFn: () => fetchTrends(activeStage),
  });

  const keywords = data?.keywords ?? [];
  const visible = keywords.slice(0, TOP_N);
  const totalCount = data?.total ?? 0;
  const moreCount = Math.max(0, totalCount - visible.length);

  return (
    <section className="px-4 mt-6 min-h-[460px]">
      <h2 className="section-title">🎯 라이프사이클 4단계</h2>

      {/* 탭 칩 — 4탭 균등 분배 (사용자 결정 2026-05-08 — 우측 여백 제거) */}
      <div className="grid grid-cols-4 gap-1.5 mb-2">
        {STAGES.map((stage) => {
          const active = stage === activeStage;
          return (
            <button
              key={stage}
              onClick={() => setActiveStage(stage)}
              className="px-2 py-1.5 rounded-full border text-2xs font-bold transition-all whitespace-nowrap text-center"
              style={{
                color: active ? "#FFFFFF" : STAGE_COLOR[stage],
                background: active ? STAGE_COLOR[stage] : STAGE_BG[stage],
                borderColor: active ? STAGE_COLOR[stage] : "transparent",
              }}
            >
              {STAGE_EMOJI[stage]} {STAGE_LABEL[stage]}
            </button>
          );
        })}
      </div>

      {/* 활성 stage 설명 — stage 이름 중복 제거 (사용자 피드백 2026-05-15) */}
      <div
        className="mb-3 px-3 py-2 rounded-lg border text-2xs leading-snug"
        style={{
          background: STAGE_BG[activeStage],
          borderColor: STAGE_BG[activeStage],
          color: STAGE_COLOR[activeStage],
        }}
      >
        <span className="text-text-sub">{STAGE_META[activeStage].tip}</span>
      </div>

      {/* 시즌 태그 (🌸 매해 / ✨ 단발) 간략 설명 — 2026-05-20 사용자 결정 */}
      <SeasonalTagLegend />

      {/* 활성 탭 키워드 list */}
      {isPending ? (
        <div className="space-y-1.5">
          <div className="text-xs text-text-sub text-center py-1 mb-1">
            📊 트렌드 키워드 분석 중... 3~5초 정도 소요됩니다
          </div>
          {Array.from({ length: TOP_N }).map((_, i) => (
            <Skeleton key={i} className="w-full h-12 rounded-xl" />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <p className="text-text-sub text-sm py-4 text-center">
          {activeStage === "mature"
            ? "베타 데이터 누적 중 — 5/22 부터 자연 발현"
            : "오늘은 해당 단계 키워드가 없어요."}
        </p>
      ) : (
        <FadeIn className="space-y-1.5" key={activeStage}>
          {visible.map((kw, i) => (
            <KeywordCard
              key={kw.id}
              kw={kw}
              rank={i + 1}
              contextStage={activeStage}
            />
          ))}

          {/* 비로그인 잠금 */}
          {moreCount > 0 && !isLoggedIn && (
            <Link
              href="/login"
              className="mt-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-primary/10 border border-primary/30 hover:bg-primary/15 transition-colors"
            >
              <span className="text-sm font-bold text-primary">
                🔒 로그인하면 {moreCount}개 더 보기
              </span>
            </Link>
          )}

          {/* 로그인 + 풀 접근 (베타 활성 또는 유료) — 전체 보기 */}
          {moreCount > 0 && isLoggedIn && hasFullAccess && (
            <Link
              href={`/trends?stage=${activeStage}`}
              className="mt-1 block text-center py-3 text-xs font-semibold text-primary hover:text-primary/80 transition-colors"
            >
              전체 보기 ({moreCount}건 더) →
            </Link>
          )}

          {/* 로그인 + 무료 (베타 종료 후) — 6위~ 잠금, 카운트 노출
              (사용자 결정 2026-05-22: TOP 5 카드 + 6위~ 카운트 + 구독 유도) */}
          {moreCount > 0 && isLoggedIn && !hasFullAccess && (
            <Link
              href="/mypage/subscribe"
              className="mt-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-primary/10 border border-primary/30 hover:bg-primary/15 transition-colors"
            >
              <span className="text-sm font-bold text-primary">
                🔒 구독하면 6위~ {moreCount}건 더 보기
              </span>
            </Link>
          )}
        </FadeIn>
      )}
    </section>
  );
}
